// URL-bar / history sync between the wrapper (top-level window) and
// the iframe where the app renders.
//
// Design:
//
//   * The wrapper owns the visible URL bar. The iframe owns the app's
//     notion of "current page". They're kept in sync via postMessage.
//
//   * Iframe → parent: the iframe-side shim (`iframe-history-shim.ts`,
//     bundled into `/_p2claw/ws-shim.js` and injected into every box
//     HTML response) monkey-patches `history.pushState` /
//     `replaceState` and listens for `popstate` / `hashchange`. Each
//     change posts a `{type: "p2claw:nav", path, title?, kind, source:
//     "iframe"}` message up. The wrapper applies it to its own
//     history with the matching primitive (`pushState` for
//     `kind=push`, `replaceState` for `replace`/`pop`/`hash`). This
//     is the default — the original wrapper handler only fired if an
//     app explicitly called `postNavEvent`, which no real-world app
//     does.
//
//   * Parent → iframe: when the user hits Back/Forward (a `popstate`
//     on the wrapper), the wrapper posts a `{… kind: "pop", source:
//     "parent"}` message DOWN. The iframe shim calls `replaceState`
//     and dispatches a synthetic `popstate` so the SPA's router
//     re-renders without reloading. Previously this used
//     `iframe.contentWindow.location.replace(…)` which tore down the
//     SPA's state on every Back/Forward.
//
//   * Loop avoidance: messages carry `source` ("iframe" | "parent");
//     each side ignores its own echoes. The iframe shim also sets a
//     local reentrancy flag during inbound application so the
//     monkey-patched `replaceState` doesn't bounce back up.
//
// Both frames are same-origin (both are served off the box's
// `<hash>.<parent>` apex), so postMessage and direct property access
// are unrestricted.

export interface NavMessage {
  type: "p2claw:nav";
  /** Path + search + hash; absolute-path form ("/foo?bar#baz"). */
  path: string;
  /** Optional document title to mirror into the outer `<title>`. */
  title?: string;
  /** How the receiver should apply the change. The original
   *  callers omitted this; the wrapper treats `undefined` as `push`
   *  for backward compatibility. */
  kind?: "push" | "replace" | "pop" | "hash";
  /** Origin tag for loop avoidance. The wrapper acts only on
   *  `source === "iframe"`; iframe acts only on `source === "parent"`.
   *  Old senders (no `source`) are treated as `iframe` since that's
   *  the only direction the original messages flowed. */
  source?: "iframe" | "parent";
}

/** Very narrow shape for the bits of the iframe we touch. */
export interface IframeLike {
  contentWindow: {
    location: { href: string; replace: (url: string) => void };
  } | null;
}

export interface InstallOptions {
  iframe: IframeLike;
  /** The wrapper window (usually `window`). */
  window: Window & typeof globalThis;
  /** Read the current app URL so `initial` pushState matches. */
  initialPath?: string;
}

/**
 * Install both halves of the bidirectional sync on the wrapper side.
 * Returns an `uninstall` function.
 */
export function installHistorySync(opts: InstallOptions): () => void {
  const { iframe, window: win } = opts;

  if (opts.initialPath) {
    win.history.replaceState({ source: "p2claw" }, "", opts.initialPath);
  }

  // Reentrancy flag on the wrapper side too — when we apply a
  // wrapper-driven update by posting DOWN to the iframe, that
  // update will cause the iframe's monkey-patched replaceState
  // (inside `iframe-history-shim.ts`) to fire. The iframe's flag
  // suppresses the outbound bounce, but if the timing is unlucky
  // (the iframe takes a tick to set its flag) the wrapper would
  // still receive the echo. Belt-and-braces de-dup: ignore inbound
  // messages whose path matches our current URL.
  const currentPath = (): string =>
    win.location.pathname + win.location.search + win.location.hash;

  const onMessage = (ev: MessageEvent): void => {
    // Only accept messages from our iframe (same origin).
    if (ev.source !== iframe.contentWindow) return;
    const data = ev.data as Partial<NavMessage> | null;
    if (!data || data.type !== "p2claw:nav") return;
    if (typeof data.path !== "string") return;
    // Ignore our own echoes (parent-tagged or omitted-and-from-parent
    // would not happen because parent-tagged messages flow DOWN, not
    // UP — but we guard anyway for symmetry with the iframe side).
    if (data.source === "parent") return;
    // Swallow if nothing changed — avoids polluting history.
    if (currentPath() === data.path) return;
    // `kind` defaults to "push" for old senders (the original). New
    // iframe shim sends explicit kind. push → new entry; everything
    // else → modify current entry.
    const kind = data.kind ?? "push";
    if (kind === "push") {
      win.history.pushState({ source: "p2claw" }, "", data.path);
    } else {
      win.history.replaceState({ source: "p2claw" }, "", data.path);
    }
    if (typeof data.title === "string") win.document.title = data.title;
  };

  const onPopState = (_ev: PopStateEvent): void => {
    // The browser already moved the URL bar. Tell the iframe to
    // mirror by posting DOWN with `kind: "pop"` + `source: "parent"`.
    // The iframe shim calls `replaceState` + dispatches a synthetic
    // popstate so the SPA's router sees the change WITHOUT a full
    // reload (the original we used `iframe.contentWindow.location.replace`
    // which tore down the SPA's state on every Back/Forward).
    const frameWin = iframe.contentWindow as
      | (Window & { location: Location })
      | null;
    if (!frameWin) return;
    const path = currentPath();
    // De-dup: nothing to do if the iframe is already there.
    try {
      if (
        frameWin.location.pathname + frameWin.location.search + frameWin.location.hash ===
        path
      ) {
        return;
      }
    } catch {
      // Cross-origin read can throw — we shouldn't be cross-origin
      // by design, but if we are, the postMessage path still works
      // (the iframe shim won't even be installed, so the message is
      // a harmless no-op).
    }
    const msg: NavMessage = {
      type: "p2claw:nav",
      path,
      kind: "pop",
      source: "parent",
    };
    frameWin.postMessage(msg, "*");
  };

  win.addEventListener("message", onMessage);
  win.addEventListener("popstate", onPopState);

  return () => {
    win.removeEventListener("message", onMessage);
    win.removeEventListener("popstate", onPopState);
  };
}

/**
 * Helper for the iframe half — apps that run inside the iframe can
 * call this instead of hand-writing the postMessage protocol. The
 * iframe typically doesn't import this directly (apps are unmodified),
 * but a small shim can inject a wrapper around `history.pushState`
 * that fires `postNavEvent()` on each push.
 */
export function postNavEvent(
  parent: Window,
  path: string,
  title?: string,
): void {
  const msg: NavMessage = { type: "p2claw:nav", path, title };
  // Target "*" is safe here because we're always same-origin; the
  // wrapper's listener validates `ev.source`.
  parent.postMessage(msg, "*");
}
